<template>
    <div class="task-page">
        <!-- Header -->
        <div class="page-hero">
            <div class="hero-left">
                <h2>报告生成任务中心</h2>
                <p class="sub">异步生成 · 灵活筛选</p>
            </div>
            <div class="hero-actions">
                <el-button type="primary" icon="el-icon-plus" @click="openNewTask">新增任务</el-button>
                <el-button icon="el-icon-refresh" :loading="loading.list" @click="fetchList">刷新</el-button>
            </div>
        </div>

        <!-- Filters -->
        <el-card class="card filters-card" shadow="hover">
            <div class="filters-grid">
                <el-input
                        v-model.trim="query.keyword"
                        placeholder="搜索：任务名  / ID"
                        clearable
                        prefix-icon="el-icon-search"
                        size="small"
                />

                <el-select v-model="query.status" placeholder="状态" clearable size="small">
                    <el-option v-for="s in statusOptions" :key="s.value" :label="s.label" :value="s.value" />
                </el-select>

                <el-select v-model="query.city" placeholder="城市" clearable filterable size="small">
                    <el-option v-for="c in cityOptions" :key="c.value" :label="c.label" :value="c.value" />
                </el-select>

                <el-select v-model="query.reportType" placeholder="报告类型" clearable size="small">
                    <el-option label="年" value="year" />
                    <el-option label="季" value="quarter" />
                    <el-option label="月" value="month" />
                </el-select>

                <el-date-picker
                        v-model="query.createdRange"
                        type="daterange"
                        range-separator="至"
                        start-placeholder="创建起"
                        end-placeholder="创建止"
                        unlink-panels
                        size="small"
                        :default-time="['00:00:00','23:59:59']"
                        value-format="yyyy-MM-dd HH:mm:ss"
                />

                <div class="filter-buttons">
                    <el-button size="small" type="primary" @click="onSearch">查询</el-button>
                    <el-button size="small" @click="onResetQuery">重置</el-button>
                </div>
            </div>
        </el-card>

        <!-- Table -->
        <el-card class="card table-card" shadow="hover">
            <div slot="header" class="card-header">
                <span>任务列表</span>
                <span class="muted">共 {{ total }} 条</span>
            </div>

            <el-table
                    :data="list"
                    border
                    stripe
                    v-loading="loading.list"
                    :row-class-name="rowClassName"
                    @sort-change="onSortChange"
                    :default-sort="{prop: query.sortProp, order: query.sortOrder==='asc'?'ascending':'descending'}"
            >
                <el-table-column type="index" width="56" label="#"/>

                <el-table-column label="任务ID" min-width="180" prop="id" sortable="custom">
                    <template slot-scope="{ row }">
                        <el-link type="primary" @click="goDetail(row)">{{ row.id }}</el-link>
                    </template>
                </el-table-column>

                <el-table-column label="任务名" min-width="180" prop="title" show-overflow-tooltip />
                <el-table-column label="城市" width="110" prop="city" />

                <el-table-column label="类型" width="90" prop="reportType">
                    <template slot-scope="{ row }">
                        <el-tag size="small" :type="typeTag(row.reportType)">{{ typeLabel(row.reportType) }}</el-tag>
                    </template>
                </el-table-column>

                <el-table-column label="时间" min-width="140">
                    <template slot-scope="{ row }">{{row.date}}</template>
                </el-table-column>

                <el-table-column label="状态" width="120" prop="status" sortable="custom">
                    <template slot-scope="{ row }">
                        <el-tag :type="statusType(row.status)">{{ statusLabel(row.status) }}</el-tag>
                    </template>
                </el-table-column>

<!--                <el-table-column label="进度" width="190">-->
<!--                    <template slot-scope="{ row }">-->
<!--                        <div class="progress-wrap">-->
<!--                            <el-progress :stroke-width="12" :percentage="row.progress || 0" :status="progressStatus(row)" />-->
<!--                        </div>-->
<!--                    </template>-->
<!--                </el-table-column>-->

                <el-table-column label="创建时间" width="170" prop="createTime" sortable="custom">
                    <template slot-scope="{ row }">{{ fmtTime(row.createTime) }}</template>
                </el-table-column>
                <el-table-column label="完成时间" width="170" prop="finishedAt" sortable="custom">
                    <template slot-scope="{ row }">{{ fmtTime(row.completionTime) }}</template>
                </el-table-column>

                <el-table-column label="操作" width="340" fixed="right">
                    <template slot-scope="{ row }">
                        <el-button size="mini" type="text" @click="openDetail(row)">详情</el-button>
                        <el-button size="mini" type="primary" :disabled="!row.resultPath" @click="download([row.resultPath])">下载生成文档</el-button>
                        <el-button size="mini" type="primary" :disabled="!row.resultPath" @click="download([row.dataPath])">下载过程文件</el-button>
                        <el-button size="mini" type="warning" :disabled="row.status!==3" @click="retry(row)">重试</el-button>
                        <el-popconfirm
                            title="确定删除该任务？"
                            @confirm="onConfirmDelete(row)"
                            @cancel="focusOutPopconfirm"
                        >
                            <el-button size="mini" type="danger" slot="reference">删除</el-button>
                        </el-popconfirm>
                    </template>
                </el-table-column>

                <template slot="empty">
                    <div class="empty">
                        <i class="el-icon-document"></i>
                        <div>暂无数据，试试调整筛选条件或新增任务</div>
                    </div>
                </template>
            </el-table>

            <div class="pager">
                <el-pagination
                        background
                        layout="total, sizes, prev, pager, next, jumper"
                        :page-size.sync="query.pageSize"
                        :page-sizes="[10,20,30,50]"
                        :current-page.sync="query.page"
                        :total="total"
                        @size-change="fetchList"
                        @current-change="fetchList"
                />
            </div>
        </el-card>

        <!-- 新增任务弹窗：沿用之前结构（简化展示） -->
        <el-dialog title="新增报告任务" :visible.sync="dlg.visible" width="720px" :close-on-click-modal="false">
            <el-form ref="dlgFormRef" :model="dlg.form" :rules="dlg.rules" label-width="110px" size="small">
                <el-form-item label="任务名" prop="title">
                    <el-input v-model.trim="dlg.form.title" maxlength="60" show-word-limit placeholder="如：海陵区-2025年8月月报" />
                </el-form-item>
                <el-form-item label="报告类型" prop="reportType">
                    <el-radio-group v-model="dlg.form.reportType">
                        <el-radio-button label="year">年报</el-radio-button>
                        <el-radio-button label="quarter">季报</el-radio-button>
                        <el-radio-button label="month">月报</el-radio-button>
                    </el-radio-group>
                </el-form-item>
                <el-form-item label="城市" prop="city">
                    <el-select v-model="dlg.form.city" placeholder="请选择城市" filterable clearable>
                        <el-option v-for="it in cityOptions" :key="it.value" :label="it.label" :value="it.value" />
                    </el-select>
                </el-form-item>
                <el-form-item v-if="dlg.form.reportType==='year'" label="年份" prop="date">
                    <el-date-picker v-model="dlg.form.date" type="year" placeholder="选择年份" style="width: 100%" />
                </el-form-item>
                <el-form-item v-if="dlg.form.reportType==='quarter'" label="季度" prop="date">
                    <el-row :gutter="8">

                        <el-date-picker
                                v-model="dlg.form.date"
                                type="daterange"
                                range-separator="至"
                                 value-format="yyyy-MM-dd"
                                start-placeholder="开始日期"
                                end-placeholder="结束日期">
                        </el-date-picker>
<!--                        <el-col :span="12">-->
<!--                            <el-form-item prop="quarterYear" label-width="0">-->
<!--                                <el-date-picker v-model="dlg.form.year" type="year" placeholder="选择年份" style="width: 100%" />-->
<!--                            </el-form-item>-->
<!--                        </el-col>-->
<!--                        <el-col :span="12">-->
<!--                            <el-form-item prop="quarter" label-width="0">-->
<!--                                <el-select v-model="dlg.form.quarter" placeholder="选择季度" style="width: 100%">-->
<!--                                    <el-option v-for="q in quarters" :key="q.value" :label="q.label" :value="q.value" />-->
<!--                                </el-select>-->
<!--                            </el-form-item>-->
<!--                        </el-col>-->
                    </el-row>
                </el-form-item>
                <el-form-item v-if="dlg.form.reportType==='month'" label="月份" prop="date">
                    <el-date-picker v-model="dlg.form.date" type="month" format="yyyy-MM" value-format="yyyy-MM" placeholder="选择月份" style="width: 100%" />
                </el-form-item>
                <el-form-item label="上传文件模板" prop="templateFile">
                    <el-upload class="uploader"
                               drag :limit="1"
                               :headers="headers"
                               :action="uploadEcxl"
                               :file-list="dlg.fileList.template"
                               :on-change="onDlgChangeTpl"
                               :on-remove="onDlgRemoveTpl"
                               :before-upload="beforeTplUpload"
                               :on-success="handleTplUploadSuccess"
                               accept=".docx,.dotx">
                        <i class="el-icon-upload"></i>
                        <div class="el-upload__text">将 .docx 模板拖到此处，或 <em>点击上传</em></div>
                    </el-upload>
                </el-form-item>
                <el-form-item label="数据来源" prop="dataFile">
                    <el-upload class="uploader" drag
                               multiple
                               :headers="headers"
                               :action="uploadEcxl"
                               :file-list="dlg.fileList.data"
                               :on-change="onDlgChangeData"
                               :on-remove="onDlgRemoveData"
                               :before-upload="beforeDataUpload"
                               :on-success="handleTplUploadSuccess"
                               accept=".xlsx,.xls,.csv,.json">
                        <i class="el-icon-upload"></i>
                        <div class="el-upload__text">将数据文件拖到此处，或 <em>点击上传</em></div>
                    </el-upload>
                </el-form-item>
            </el-form>
            <span slot="footer" class="dialog-footer">
        <el-button @click="dlg.visible=false">取 消</el-button>
        <el-button type="primary" :loading="loading.create" @click="createTask">创 建</el-button>
      </span>
        </el-dialog>


        <!-- 详情抽屉 -->
        <el-drawer
                title="任务详情"
                :visible.sync="detailVisible"
                direction="rtl"
                size="50%"
                custom-class="detail-drawer"
                :before-close="()=>{detailVisible=false}"
        >
            <div v-if="detail" class="detail-body">
                <div class="detail-desc">
                    <el-row :gutter="20">
                        <el-col :span="12">
                            <div class="detail-item">
                                <span class="label">任务ID：</span>
                                <span class="value">{{ detail.id }}</span>
                            </div>
                        </el-col>
                        <el-col :span="12">
                            <div class="detail-item">
                                <span class="label">任务名：</span>
                                <span class="value">{{ detail.title }}</span>
                            </div>
                        </el-col>
                        <el-col :span="12">
                            <div class="detail-item">
                                <span class="label">城市：</span>
                                <span class="value">{{ detail.city }}</span>
                            </div>
                        </el-col>
                    </el-row>

                    <el-row :gutter="20">
                        <el-col :span="12">
                            <div class="detail-item">
                                <span class="label">报告类型：</span>
                                <span class="value">{{ typeLabel(detail.reportType) }}</span>
                            </div>
                        </el-col>
                        <el-col :span="12">
                            <div class="detail-item">
                                <span class="label">状态：</span>
                                <el-tag :type="statusType(detail.status)" effect="dark">{{ statusLabel(detail.status )}}</el-tag>
                            </div>
                        </el-col>
                    </el-row>
                </div>


                <!-- 底部操作 -->
                <div class="detail-footer">
                    <el-button
                            v-if="detail.dataSourcePath && detail.dataSourcePath.length"
                            type="primary"
                            icon="el-icon-download"
                            size="small"
                            @click="download(detail.dataSourcePath)"
                    >
                        下载数据来源文档
                    </el-button>
                    <el-tag v-else type="info">暂无文档</el-tag>
                </div>

                <!-- 底部操作 -->
                <div class="detail-footer">
                    <el-button
                            v-if="detail.resultPath"
                            type="primary"
                            icon="el-icon-download"
                            size="small"
                            @click="download([detail.resultPath])"
                    >
                        下载文档
                    </el-button>
                    <el-tag v-else type="info">暂无文档</el-tag>
                </div>
            </div>

            <!-- 骨架屏 -->
            <el-skeleton v-else :rows="6" animated />
        </el-drawer>

    </div>
</template>

<script>
    import axios from 'axios'
    import url from "../../../utils/UrlApi"
    import {saveAs} from "file-saver";
    export default {
        name: 'ReportTaskListEnhanced',
        data() {
            return {
                headers: {
                    Authorization: localStorage.getItem('token')
                },
                uploadEcxl: url.uploadReportBot,

                list: [],
                total: 0,
                timer: null,
                loading: { list: false, create: false },
                query: {
                    page: 1,
                    pageSize: 10,
                    keyword: '',
                    status: '',
                    city: '',
                    reportType: '',
                    createdRange: [],
                    sortProp: 'createTime',
                    sortOrder: 'desc',
                },
                statusOptions: [
                    { label: '排队中', value: 0 },
                    { label: '执行中', value: 1 },
                    { label: '已完成', value: 2 },
                    { label: '失败', value: 3 },
                    // { label: '已取消', value: 'canceled' },
                ],
                cityOptions: [
                    { label: '海陵区', value: '海陵区' },
                    { label: '乌海市', value: '乌海市' },
                ],
                quarters: [
                    { label: '第一季度 (Q1)', value: 'Q1' },
                    { label: '第二季度 (Q2)', value: 'Q2' },
                    { label: '第三季度 (Q3)', value: 'Q3' },
                    { label: '第四季度 (Q4)', value: 'Q4' },
                ],
                dlg: {
                    visible: false,
                    form: {
                        title: '',
                        reportType: 'month',
                        city: '',
                        year: '',
                        quarterYear: '',
                        quarter: '',
                        month: '',
                        templateFile: null,
                        dataFile: null,
                    },
                    fileList: { template: [], data: [] },
                    rules: {
                        title: [{ required: true, message: '请输入任务名', trigger: 'blur' }],
                        reportType: [{ required: true, message: '请选择类型', trigger: 'change' }],
                        city: [{ required: true, message: '请选择城市', trigger: 'change' }],
                        date: [{ required: true, message: '请选择时间', trigger: 'change' }],
                        // year: [{ validator: (r,v,cb)=>this.valYear(this.dlg.form,cb), trigger: 'change' }],
                        // quarter: [{ validator: (r,v,cb)=>this.valQuarter(this.dlg.form,cb), trigger: 'change' }],
                        // month: [{ validator: (r,v,cb)=>this.valMonth(this.dlg.form,cb), trigger: 'change' }],
                        templateFile: [{ validator: (r,v,cb)=>this.valFile('template',cb), trigger: 'change' }],
                        dataFile: [{ validator: (r,v,cb)=>this.valFile('data',cb), trigger: 'change' }],
                    }
                },
                detailVisible: false,
                detail: null,
            }
        },
        created() {
            this.fetchList()
        },
        mounted() {
            // this.startPolling()
        },
        beforeDestroy() { this.stopPolling() },
        methods: {
            // ======= API =======
            async fetchList() {
                this.loading.list = true
                // try {
                //     const { page, pageSize, keyword, status, city, reportType, createdRange, sortProp, sortOrder } = this.query
                //     const params = { page, pageSize, keyword, status, city, reportType, sortProp, sortOrder }
                //     if (createdRange && createdRange.length === 2) {
                //         params.createdFrom = createdRange[0]
                //         params.createdTo = createdRange[1]
                //     }
                //     const { data } = await axios.get('/api/report/tasks', { params })
                //     this.list = (data.items || []).map(it => ({ progress: 0, ...it }))
                //     this.total = data.total || 0
                // } catch (e) {
                //     this.$message.error(`获取任务失败：${e?.response?.data?.message || e.message}`)
                // } finally {
                //     this.loading.list = false
                // }
                const { page, pageSize, keyword, status, city, reportType, createdRange, sortProp, sortOrder } = this.query
                const params = { page, pageSize, keyword, status, city, reportType, sortProp, sortOrder }
                if (createdRange && createdRange.length === 2) {
                    params.createdFrom = createdRange[0]
                    params.createdTo = createdRange[1]
                }
                this.$http.get(url.pageReportBot, {params: params}).then(res => {
                    let data = res.data
                    if (data.code === '200') {
                        this.list = (data.data.list || [])
                        this.total = data.data.totalCount || 0
                        this.loading.list = false
                    } else {
                        this.$message.error(data.data.message)
                    }
                })




            },
            startPolling() { this.stopPolling(); this.timer = setInterval(this.pollTick, 3000) },
            stopPolling() { if (this.timer) { clearInterval(this.timer); this.timer = null } },
            async pollTick() {
                // const runningIds = this.list.filter(x => ['queued','running'].includes(x.status)).map(x => x.id)
                // if (runningIds.length === 0) return
                // try {
                    // const { data } = await axios.get('/api/report/tasks/batch', { params: { ids: runningIds.join(',') } })
                    // const map = new Map((data.items||[]).map(x => [x.id, x]))
                    // this.list = this.list.map(row => map.get(row.id) ? { ...row, ...map.get(row.id) } : row)
                // } catch (e) { console.warn('poll error', e.message) }
            },

            // ======= Filters =======
            onSearch() { this.query.page = 1; this.fetchList() },
            onResetQuery() {
                this.query = { ...this.query, page: 1, keyword: '', status: '', city: '', reportType: '', createdRange: [], sortProp: 'createTime', sortOrder: 'desc' }
                this.fetchList()
            },
            onSortChange({ prop, order }) {
                this.query.sortProp = prop
                this.query.sortOrder = order === 'ascending' ? 'asc' : 'desc'
                this.fetchList()
            },

            // ======= Actions =======
            openNewTask() {
                // console.log(this.uploadEcxl)
                this.dlg.visible = true;
                this.$nextTick(()=> this.$refs.dlgFormRef && this.$refs.dlgFormRef.clearValidate()) },

            async createTask() {
                this.$refs.dlgFormRef.validate(async valid => {
                    if (!valid) return
                    try {
                        this.loading.create = true
                        console.log(this.dlg.form)
                        let fileList= []
                        this.dlg.form.dataFile.forEach(item=>{
                            fileList.push(item.response.data.path)
                        })

                        let data = {
                            title:this.dlg.form.title,
                            templatePath:this.dlg.form.templateFile[0].response.data.path,
                            reportType:this.dlg.form.reportType,
                            city:this.dlg.form.city,
                            date: this.dlg.form.reportType==='quarter' ?  this.dlg.form.date.toString() : this.dlg.form.date,
                            // year:this.dlg.form.year,
                            // season:this.dlg.form.quarter,
                            // month:this.dlg.form.month,
                            dataSourcePath:fileList,
                        }
                        // const fd = new FormData()
                        // fd.append('title', this.dlg.form.title)
                        // fd.append('city', this.dlg.form.city)
                        // fd.append('reportType', this.dlg.form.reportType)
                        // if (this.dlg.form.reportType === 'year') {
                        //     const y = this.$moment ? this.$moment(this.dlg.form.year).format('YYYY') : new Date(this.dlg.form.year).getFullYear()
                        //     fd.append('year', y)
                        // } else if (this.dlg.form.reportType === 'quarter') {
                        //     const y = this.$moment ? this.$moment(this.dlg.form.quarterYear).format('YYYY') : new Date(this.dlg.form.quarterYear).getFullYear()
                        //     fd.append('year', y)
                        //     fd.append('quarter', this.dlg.form.quarter)
                        // } else if (this.dlg.form.reportType === 'month') {
                        //     fd.append('month', this.dlg.form.month)
                        // }
                        // fd.append('template', this.dlg.form.templateFile)
                        // fd.append('data', this.dlg.form.dataFile)


                        this.$http.post(url.generateReportBot, data).then(res => {
                            if (res.data.code === '200') {
                                this.$message.success('任务已创建，进入排队/执行中…')
                                this.dlg.visible = false
                                this.fetchList()
                            } else {
                                this.$message.error(res.data.message)
                            }
                        })
                        // await axios.post('/api/report/tasks', fd, { headers: { 'Content-Type': 'multipart/form-data' } })
                        // this.$message.success('任务已创建，进入排队/执行中…')
                        // this.dlg.visible = false
                        // this.fetchList()
                    } catch (e) {
                        // this.$message.error(`创建失败：${e?.response?.data?.message || e.message}`)
                    } finally { this.loading.create = false }
                })
            },
            openDetail(row){
                console.log(row)
                this.detailVisible = true
                this.detail = row
            },
            download(fileList) {
                fileList.forEach(item=>{
                    this.$http.get(url.downloadReportBot,
                        {params: {path:item}, responseType: 'blob'}
                        ).then(res => {
                       console.log(res)
                        const fileName = item.split('/').pop();
                        saveAs(res.data, fileName);
                    })
                })
            },
             retry(row) {
                this.$http.post(url.retryReportBot+row.id).then(res => {
                    if (res.data.code === '200') {
                        this.$message.success('重新执行任务');
                        this.fetchList()
                    } else {
                        this.$message.error(res.data.message)
                    }
                })
                },
            async cancel(row) { try { await axios.post(`/api/report/tasks/${row.id}/cancel`); this.$message.success('已取消'); this.fetchList() } catch (e) { this.$message.error(`取消失败：${e?.response?.data?.message || e.message}`) } },

            /** 在 popconfirm 关闭前将焦点移出，避免 aria-hidden 与焦点共存触发无障碍警告 */
            focusOutPopconfirm() {
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
            },
            onConfirmDelete(row) {
                this.focusOutPopconfirm();
                this.remove(row);
            },
            remove(row) {
                this.$http.delete(url.deleteReportBot+row.id).then(res => {
                    if (res.data.code === '200') {
                        this.$message.success('已删除');
                        this.fetchList()
                    } else {
                        this.$message.error(res.data.message)
                    }
                })
            },

            // ======= Display helpers =======
            typeTag(t) { return { year: 'info', quarter: 'warning', month: '' }[t] || '' },
            typeLabel(t) { return { year: '年报', quarter: '季报', month: '月报' }[t] || t },
            displayPeriod(row) { if (row.reportType==='year') return `${row.year}`; if (row.reportType==='quarter') return `${row.year}-${row.quarter}`; if (row.reportType==='month') return `${row.month}`; return '-' },
            statusType(s) {
                return {
                    0: 'info',
                    1: 'warning',
                    2: 'success',
                    3: 'danger',
                    4: 'info' }[s] || 'info' },
            statusLabel(s) { return {
                0: '排队中',
                1: '执行中',
                2: '已完成',
                3: '已失败',
                4: '已取消' }[s] || s },
            progressStatus(row) { return row.status==='failed' ? 'exception' : (row.status==='success' ? 'success' : '') },
            fmtTime(ts) { if (!ts) return '-'; try { return this.$moment ? this.$moment(ts).format('YYYY-MM-DD HH:mm') : new Date(ts).toLocaleString() } catch { return ts } },
            rowClassName({ row }) { return `row--${row.status || 'default'}` },

            // ======= Dialog upload handlers =======
            onDlgChangeTpl(file, fileList) {
                // console.log(file)
                // console.log(fileList)
                this.dlg.fileList.template = fileList;
                this.dlg.form.templateFile = fileList
            },
            onDlgRemoveTpl(file, fileList) { this.dlg.fileList.template = fileList; this.dlg.form.templateFile = fileList[0]?.raw || null },
            onDlgChangeData(file, fileList) { this.dlg.fileList.data = fileList; this.dlg.form.dataFile = fileList },
            onDlgRemoveData(file, fileList) { this.dlg.fileList.data = fileList; this.dlg.form.dataFile = fileList[0]?.raw || null },
            beforeTplUpload(file) {
                const ok = /\.(docx|dotx)$/i.test(file.name);
                if (!ok) this.$message.error('模板仅支持 .docx/.dotx');
                return ok },
            handleTplUploadSuccess(response, file, fileList){
                console.log(response)
                if (response.code === "200") {
                    this.$message.success("上传成功");
                } else {
                    this.$message.error("上传失败：" + (response.msg || "服务器返回错误"));
                    // 手动移除无效文件
                    fileList.splice(fileList.indexOf(file), 1);
                }
            },
            beforeDataUpload(file) { const ok = /\.(xlsx|xls|csv|json)$/i.test(file.name); if (!ok) this.$message.error('数据仅支持 .xlsx/.xls/.csv/.json'); return ok },


            uploadSectionFile(params){
                console.log(params)
                const loading = this.$loading({
                    lock: true,
                    text: '导入中',
                    spinner: 'el-icon-loading',
                });
                const data = new FormData();
                data.append(params.filename, params.file);
                request.post(params.action, data, {
                    headers: { 'Content-Type': 'multipart/form-data', Authorization: localStorage.getItem('token') },
                    responseType: 'blob'
                })
                    .then(resp => {
                        loading.close();
                        console.log(params);
                        console.log(resp);


                        this.$message.success('上传成功')
                        let fileName= decodeURI(resp.headers.name)
                        // console.log(fileName)
                        // saveAs(resp.data, fileName);

                    }).catch(err => {
                    loading.close();
                    console.log(err);
                });

            },
            handleExceed(){
                this.$message.error('超出文件限制个数: 1 个')
            },
            handlesuccess(response, file, fileList) {
                console.log(response)
                if (response.code === '200') {
                    this.flieData = response.data
                } else {
                    this.$message.error('上传失败')
                    this.fileList = []
                }
            },



            // ======= Validators =======
            valFile(kind, cb) { const list = this.dlg.fileList[kind]; if (!list || list.length===0) return cb(new Error('请上传文件')); const file = list[0]; if (kind==='template' && !/\.(docx|dotx)$/i.test(file.name)) return cb(new Error('模板仅支持 .docx/.dotx')); if (kind==='data' && !/\.(xlsx|xls|csv|json)$/i.test(file.name)) return cb(new Error('数据仅支持 .xlsx/.xls/.csv/.json')); cb() },
            valYear(form, cb) { if (form.reportType!=='year') return cb(); if (!form.year) return cb(new Error('请选择年份')); cb() },
            valQuarter(form, cb) { if (form.reportType!=='quarter') return cb(); if (!form.quarterYear) return cb(new Error('请选择年份')); if (!form.quarter) return cb(new Error('请选择季度')); cb() },
            valMonth(form, cb) { if (form.reportType!=='month') return cb(); if (!form.month) return cb(new Error('请选择月份')); cb() },
        }
    }
</script>

<style lang="less" scoped>
    .task-page { padding: 16px 18px 28px; background: #f5f7fa; }

    .page-hero {
        display: flex; align-items: center; justify-content: space-between;
        padding: 16px 18px; margin-bottom: 12px;
        background: linear-gradient(135deg, #eef2ff 0%, #f7fee7 100%);
        border: 1px solid #eef1f6; border-radius: 14px;
    }
    .page-hero h2 { margin: 0; font-weight: 700; font-size: 20px; color: #1f2d3d; }
    .page-hero .sub { margin: 4px 0 0; color: #65748b; font-size: 13px; }
    .page-hero .hero-actions > * + * { margin-left: 8px; }

    .card { border-radius: 12px; overflow: hidden; }
    .card .card-header { display: flex; align-items: center; justify-content: space-between; font-weight: 600; }
    .card .card-header .muted { color: #909399; font-size: 12px; }

    .filters-card { position: sticky; top: 10px; z-index: 5; margin-bottom: 12px; }
    .filters-grid { display: grid; grid-template-columns: minmax(220px, 1.2fr) 160px 160px 140px 330px auto; grid-gap: 10px; align-items: center; }
    .filter-buttons { display: flex; align-items: center; }

    .table-card { }
    .progress-wrap { padding-right: 6px; }

    .pager { margin-top: 12px; text-align: right; }

    .empty { padding: 40px 0; color: #98a2b3; display: flex; flex-direction: column; align-items: center; }
    .empty i { font-size: 36px; margin-bottom: 8px; }

    /* Row status coloring */
    ::v-deep .el-table .row--running > td { background: #fffaf0; }
    ::v-deep .el-table .row--failed > td { background: #fff5f5; }
    ::v-deep .el-table .row--success > td { background: #f6ffed; }

    /* Minor tweaks for compact form/table */
    ::v-deep .el-form-item { margin-bottom: 12px; }
    ::v-deep .el-table th, ::v-deep .el-table td { padding: 8px 10px; }

    /* Responsive */
    @media (max-width: 1366px) {
        .filters-grid { grid-template-columns: minmax(200px,1fr) 140px 140px 120px 280px auto; }
    }
    @media (max-width: 1024px) {
        .filters-grid { grid-template-columns: 1fr 1fr; }
    }

    .detail-drawer {
        background: #fdfdfd;
        border-top-left-radius: 12px;
        border-bottom-left-radius: 12px;
        box-shadow: -3px 0 8px rgba(0, 0, 0, 0.08);

        .el-drawer__header {
            font-size: 18px;
            font-weight: 600;
            color: #1f2d3d;
            margin-bottom: 8px;
        }

        .detail-body {
            padding: 16px;
        }

        .detail-desc {
            background: #fff;
            border-radius: 8px;
            padding: 12px;
            .el-descriptions__label {
                font-weight: 500;
                color: #606266;
            }
            .el-descriptions__content {
                color: #303133;
            }
        }

        .detail-footer {
            margin-top: 20px;
            text-align: right;
        }
    }
    .detail-desc {
        background: #fff;
        border-radius: 8px;
        padding: 12px;

        .detail-item {
            margin-bottom: 12px;

            .label {
                font-weight: 500;
                color: #606266;
                margin-right: 6px;
            }

            .value {
                color: #303133;
            }
        }
    }

</style>
